import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface RegistroMapaOffSetting {
  /** Las empresas con el Registro MAPA APAGADO. Sin la clave o vacía: ninguna. */
  companies?: string[];
}

/**
 * La MISMA regla que `public._registro_mapa_off()` en la base de datos
 * (migración 20260926100300): si cambia una, cambia la otra.
 *
 * Al revés que `pest_foci`, aquí la lista es de las que lo tienen APAGADO: el
 * Registro MAPA está encendido para todas, y quitarle a una empresa algo que
 * funciona es la excepción. Sin fila, sin lista o sin empresa → encendido.
 */
export function registroMapaOffFor(
  setting: RegistroMapaOffSetting | null | undefined,
  companyId: string | null | undefined,
): boolean {
  if (!companyId) return false;
  const companies = Array.isArray(setting?.companies) ? setting.companies : [];
  return companies.includes(companyId);
}

/**
 * ¿Tiene ESTA empresa apagado el Registro Fitosanitario del MAPA? (D26 del
 * cuaderno, 26-09-2026.)
 *
 * Chinampa Farms trabaja con registros de México (RSCO) aunque figure como
 * España: para ella las sugerencias del MAPA, el relleno automático del nº y el
 * aviso de «No vigente» son ruido, y validar sus nº contra el MAPA los tacharía
 * todos. Apagado:
 *  · alta y ficha: ni sugerencias ni relleno automático (el catálogo ni se pide);
 *  · salidas (PDF de aplicaciones, notas de consumo, descarga del inventario):
 *    el resolvedor con `mapaValidation: false`, que imprime su código tal cual.
 *
 * Mismo sitio y forma de leerlo que `useFociEnabled`: la fila `registro_mapa_off`
 * de `platform_settings` (legible con la sesión desde la migración 20260926160000:
 * la política es una lista de claves, B1), que es la que mira también la RPC del
 * alta. El cargador del cuaderno pregunta a la RPC `_registro_mapa_off`. Mientras
 * carga, `loading`: quien decide si enseña sugerencias espera (ante la duda, no
 * se ofrece un nº del MAPA a quien no lo quiere); quien imprime usa `off`.
 */
export function useRegistroMapaOff() {
  const { company } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["registro-mapa-off"],
    queryFn: async (): Promise<RegistroMapaOffSetting | null> => {
      const { data } = await supabase
        .from("platform_settings")
        .select("value")
        .eq("key", "registro_mapa_off")
        .maybeSingle();
      return ((data as { value?: RegistroMapaOffSetting } | null)?.value ?? null) as RegistroMapaOffSetting | null;
    },
    staleTime: 5 * 60_000,
  });
  return { off: registroMapaOffFor(data, company?.id), loading: isLoading };
}
